import { CommonModule } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { AccordionComponent } from '../../shared/components/accordion/accordion.component';
import { RadioComponent } from '../../shared/components/form-controls/radio/radio.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Formly field type that renders a collapsible accordion containing a responsive
 * grid of radio buttons.
 *
 * Props:
 * - `options`            – `{ value: any; label: string }[]`  Radio options (standard Formly options array)
 * - `columns`            – `2 | 3 | 4`  Number of responsive columns (default: `4`)
 * - `isOpen`             – `boolean`     Whether the accordion starts expanded (default: `false`)
 * - `label`              – `string`      Fallback header text when nothing is selected
 * - `selectedListPrefix` – `string`      Optional prefix shown before the selected option label in the accordion header
 *
 * The accordion header displays the label of the currently selected option,
 * optionally prefixed by `props.selectedListPrefix`, falling back to `props.label`
 * (or an empty string) when nothing is selected.
 *
 * Usage in a FormlyFieldConfig:
 * ```typescript
 * {
 *   key: 'lookupListId',
 *   type: 'collapsible-radio-list',
 *   props: {
 *     label: 'formBuilder.lookupList',
 *     columns: 3,
 *     isOpen: false,
 *     options: myOptions,          // or bound via expressionProperties
 *   },
 * }
 * ```
 */
@Component({
  selector: 'app-collapsible-radio-list-formly-type',
  standalone: true,
  imports: [CommonModule, FormsModule, AccordionComponent, RadioComponent, TranslatePipe],
  templateUrl: './collapsible-radio-list-formly-type.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './collapsible-radio-list-formly-type.component.css',
})
export class CollapsibleRadioListFormlyTypeComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  selectedValue: any = null;

  ngOnInit(): void {
    this.selectedValue = this.formControl.value ?? null;
  }

  get columns(): 2 | 3 | 4 {
    const c = this.props['columns'];
    return c === 2 || c === 3 || c === 4 ? c : 4;
  }

  get columnsClass(): string {
    return `col-${this.columns}`;
  }

  get groupName(): string {
    if (this.field.id) return this.field.id;
    if (this.field.key) return this.field.key.toString();
    return 'radio-group';
  }

  get selectedLabel(): string {
    const options = (this.props.options as { value: any; label: string }[] | undefined) ?? [];
    const selectedOption = options.find((o) => o.value === this.selectedValue);

    if (selectedOption) {
      const prefix = this.props['selectedListPrefix'] as string | undefined;
      return prefix ? `${prefix} ${selectedOption.label}` : selectedOption.label;
    }

    return this.props.label ?? '';
  }

  onSelectionChange(newValue: any): void {
    this.selectedValue = newValue;
    if (newValue !== this.formControl.value) {
      this.formControl.setValue(newValue);
      this.formControl.markAsDirty();
    }
  }
}
